import classNames from 'classnames';
import React, { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';

import { sanitizeAccountNumber, validAccountNumber } from '@eika-infrastruktur/format';
import { Button, RowButton } from '@eika/button';
import { ErrorCard } from '@eika/card';
import { Icon, IconTypes } from '@eika/icon';
import { SearchInput } from '@eika/input';
import { Label } from '@eika/label';
import { RichIcon, RichIconTypes } from '@eika/rich-icon';

import type { Receiver } from '../../../../generated-types';
import useTranslation from '../../../../hooks/useTranslation';
import { KeyboardPadding } from '../../../KeyboardPadding';
import { SelectList } from '../../../shared/SelectList';
import { PartialReceiver } from '../../SelectedReceiver';
import NewReceiver from './NewReceiver';
import ReceiverListItem from './ReceiverListItem';
import SelectReceiverStickyFooter from './SelectReceiverStickyFooter';

import './SelectReceiverForm.scss';

type RequestExceptionWithResponse = import('@eika-infrastruktur/request').RequestExceptionWithResponse;

interface Props {
    onChange: (receiverAccount: PartialReceiver) => void;
    receivers?: Receiver[];
    refresh: () => void;
    error?: RequestExceptionWithResponse;
}

const SelectReceiverForm: React.FC<Props> = ({ onChange, receivers, refresh, error }) => {
    const translated = useTranslation();
    const [searchString, setSearchString] = useState<string>('');
    const [searchMatchesReceiver, setSearchMatchesReceiver] = useState(false);
    const [addReceiverClicked, setAddReceiverClicked] = useState(false);
    const [validationMessage, setValidationMessage] = useState<string | undefined>();
    const [pending, setPending] = useState(false);

    const accountNumberList = useMemo(
        () => receivers?.map((account) => account.accountNumber.value) ?? [],
        [receivers],
    );

    useEffect(() => {
        setValidationMessage(undefined);
    }, [searchString]);

    useEffect(() => {
        setSearchMatchesReceiver(accountNumberList.includes(sanitizeAccountNumber(searchString)));
    }, [accountNumberList, searchString]);

    const filteredAccountsList = receivers?.filter((account) => {
        const searchStringWithoutSeparators = searchString.replace(/[\s.]+/g, '');
        return (
            account.accountNumber.value.includes(searchStringWithoutSeparators) ||
            account.name.toLowerCase().includes(searchString.toLowerCase())
        );
    });

    const handleSubmit = useCallback(async () => {
        setPending(true);
        const searchStringSanitized = sanitizeAccountNumber(searchString);
        const isNumeric = !Number.isNaN(+searchStringSanitized);
        try {
            if (!isNumeric) {
                setAddReceiverClicked(true);
                return;
            }

            if (!searchStringSanitized || !validAccountNumber(searchStringSanitized)) {
                setValidationMessage(translated.paymentValidation.invalidAccountNumber);
                return;
            }

            if (searchMatchesReceiver) {
                onChange({
                    accountNumber: { value: searchStringSanitized },
                    name: filteredAccountsList?.[0]?.name,
                });
            } else {
                onChange({ accountNumber: { value: searchStringSanitized } });
            }
        } finally {
            setPending(false);
        }
    }, [
        filteredAccountsList,
        onChange,
        searchMatchesReceiver,
        searchString,
        translated.paymentValidation.invalidAccountNumber,
    ]);

    return addReceiverClicked ? (
        <NewReceiver receivers={receivers} searchString={searchString} onChange={onChange} />
    ) : (
        <div className="select-receiver-form__container">
            <div
                className={classNames('select-receiver-form__search-field', {
                    'with-label': filteredAccountsList?.length === 0 || validationMessage,
                })}
            >
                <SearchInput
                    placeholder={translated.receiverSearch}
                    value={searchString}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => setSearchString(event.target.value)}
                    className="select-receiver-form__search-input"
                    maxLength={35}
                    aria-label={translated.receiverSearch}
                />
            </div>
            {validationMessage && (
                <div className="select-receiver-form__info-label-wrapper">
                    <Label type="error" text={validationMessage} />
                </div>
            )}
            {error ? (
                <ErrorCard
                    className="select-receiver-form__fetching-error"
                    title={translated.errors.sorry}
                    exception={error}
                    description={translated.errors.somethingWentWrongFetchingReceivers}
                    icon={<RichIcon type={RichIconTypes.NETTVERKSBRUDD_HARE} />}
                    isModal
                >
                    <Button className="select-receiver-form__button-error" onClick={refresh}>
                        {translated.tryAgain}
                    </Button>
                </ErrorCard>
            ) : (
                <SelectList>
                    {filteredAccountsList?.map((receiver) => (
                        <li key={receiver.id.value}>
                            <ReceiverListItem receiver={receiver} onClick={() => onChange(receiver)} />
                        </li>
                    ))}
                    {!searchMatchesReceiver && (
                        <li key="addNewReceiver">
                            <RowButton
                                onClick={() => setAddReceiverClicked(true)}
                                className="select-receiver-form__add-receiver-row"
                            >
                                <span className="add-icon">
                                    <Icon
                                        type={IconTypes.PLUSS_24}
                                        className="icon--24 select-receiver-form__pluss-icon"
                                    />
                                </span>
                                <span>{translated.addNewReceiver}</span>
                            </RowButton>
                        </li>
                    )}
                </SelectList>
            )}
            <KeyboardPadding />
            <SelectReceiverStickyFooter onClickButton={handleSubmit} pending={pending} />
        </div>
    );
};

export default SelectReceiverForm;
